import type {
  PaywallInfo,
  SuperwallEventInfo,
  TransactionProductIdentifier,
} from "@-/com.npmjs.expo-superwall";
import type {
  AnalyticsProperties,
  SuperwallAnalyticsEventName,
} from "@-/dev.improve.haniverse.lib.analytics";

export type SuperwallAnalyticsEvent = {
  name: SuperwallAnalyticsEventName;
  properties: AnalyticsProperties;
};

/**
 * The paywall's identity and how it was reached. Typed as partial because the native bridge can
 * deliver an incomplete payload; a missing field becomes `null` rather than a thrown capture.
 */
function paywallProperties(paywallInfo: Partial<PaywallInfo> | undefined): AnalyticsProperties {
  return {
    experiment_group_id: paywallInfo?.experiment?.groupId ?? null,
    experiment_id: paywallInfo?.experiment?.id ?? null,
    experiment_variant_id: paywallInfo?.experiment?.variant?.id ?? null,
    is_free_trial_available: paywallInfo?.isFreeTrialAvailable ?? null,
    paywall_id: paywallInfo?.identifier ?? null,
    paywall_name: paywallInfo?.name ?? null,
    placement: paywallInfo?.presentedByEventWithName ?? null,
    presented_by: paywallInfo?.presentedBy ?? null,
    product_ids: paywallInfo?.productIds ?? [],
  };
}

function productProperties(
  product: Partial<TransactionProductIdentifier> | undefined,
): AnalyticsProperties {
  return { product_id: product?.productIdentifier ?? null };
}

/**
 * Maps the Superwall SDK events that make up the client half of the paywall funnel to PostHog
 * events; every other SDK event (config, webview loads, attributes, …) returns `null`. Revenue,
 * trials, renewals and churn come from Superwall's server-side PostHog integration (`sw_*`), so
 * these events carry no prices and are never used to count money.
 */
export function mapSuperwallAnalyticsEvent({
  event,
}: Pick<SuperwallEventInfo, "event">): SuperwallAnalyticsEvent | null {
  switch (event.event) {
    case "paywallOpen": {
      return { name: "superwall_paywall_open", properties: paywallProperties(event.paywallInfo) };
    }
    case "paywallClose": {
      return {
        name: "superwall_paywall_close",
        properties: {
          ...paywallProperties(event.paywallInfo),
          close_reason:
            (event.paywallInfo as Partial<PaywallInfo> | undefined)?.closeReason ?? null,
        },
      };
    }
    case "paywallDecline": {
      return {
        name: "superwall_paywall_decline",
        properties: paywallProperties(event.paywallInfo),
      };
    }
    case "transactionStart": {
      return {
        name: "superwall_transaction_start",
        properties: {
          ...paywallProperties(event.paywallInfo),
          ...productProperties(event.product),
        },
      };
    }
    case "transactionComplete": {
      return {
        name: "superwall_transaction_complete",
        properties: {
          ...paywallProperties(event.paywallInfo),
          ...productProperties(event.product),
          transaction_type: event.type,
        },
      };
    }
    case "transactionAbandon": {
      return {
        name: "superwall_transaction_abandon",
        properties: {
          ...paywallProperties(event.paywallInfo),
          ...productProperties(event.product),
        },
      };
    }
    case "transactionFail": {
      return {
        name: "superwall_transaction_fail",
        // The store's error description; it names the failure, never the user.
        properties: { ...paywallProperties(event.paywallInfo), error: event.error },
      };
    }
    case "transactionRestore": {
      return {
        name: "superwall_transaction_restore",
        properties: {
          ...paywallProperties(event.paywallInfo),
          restore_type:
            (event.restoreType as Partial<typeof event.restoreType> | undefined)?.type ?? null,
        },
      };
    }
    case "transactionTimeout": {
      return {
        name: "superwall_transaction_timeout",
        properties: paywallProperties(event.paywallInfo),
      };
    }
    case "freeTrialStart": {
      return {
        name: "superwall_free_trial_start",
        properties: {
          ...paywallProperties(event.paywallInfo),
          ...productProperties(event.product),
        },
      };
    }
    case "subscriptionStart": {
      return {
        name: "superwall_subscription_start",
        properties: {
          ...paywallProperties(event.paywallInfo),
          ...productProperties(event.product),
        },
      };
    }
    case "subscriptionStatusDidChange": {
      const status = event.subscriptionStatus as
        | Partial<typeof event.subscriptionStatus>
        | undefined;
      return {
        name: "superwall_subscription_status_changed",
        properties: {
          entitlement_ids:
            status !== undefined && "entitlements" in status && status.entitlements !== undefined
              ? status.entitlements.map((entitlement) => entitlement.id)
              : [],
          subscription_status: status?.status ?? null,
        },
      };
    }
    default: {
      return null;
    }
  }
}
